import Subscription from '../../../../domain/models/Subscription';
import { Alert, Button, Checkbox, Flex, Form, message, notification, Radio, Select, Table, Tooltip } from 'antd';
import { FormCard, FormSectionTitle } from '@leedpulse-monorepo/components';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import SubscriptionEditionFormContext from '../../../../presentation/contexts/forms/subscriptions/SubscriptionEditionFormContext';
import Card from 'antd/es/card/Card';
import AuthenticationContext from '../../../../presentation/contexts/auth/AuthenticationContext';
import {
  DelayType,
  delayTypeAsString,
  ProspectType,
  prospectTypeAsString,
  UserRole,
  partnersTypeAsString,
  PartnersType,
  ContextStatus,
} from '@leedpulse-monorepo/enums';
import { ArrowDownOutlined, ArrowUpOutlined, ClockCircleOutlined, CloudDownloadOutlined, ContactsOutlined, LoadingOutlined, MailOutlined, RobotOutlined, RocketOutlined, ScheduleOutlined, SearchOutlined, SettingOutlined, SyncOutlined, UserOutlined } from '@ant-design/icons';
import importCRMContacts from '../../../../domain/use_cases/contacts/importCRMContacts';
import ScenariosContext from '../../../../presentation/contexts/data/ScenariosContext';
import getDryRunReport from '../../../../domain/use_cases/contacts/getDryRunReport';
import SubscriptionDataSource from '../../../../domain/enums/subscription/subscription_datasource/SubscriptionDataSource';
import generateCrmSnapshot from '../../../../domain/use_cases/contacts/generateCrmSnapshot';
import getCrmSnapshotStatus, { GetCrmSnapshotStatusResponse } from '../../../../domain/use_cases/contacts/getCrmSnapshotStatus';
import CrmContactExplorerModal from './CrmContactExplorerModal';
interface CRMDetailsProps {
  subscription: Subscription;
}

const CRMDetails: React.FC<CRMDetailsProps> = ({ subscription }) => {
  const formContext = useContext(SubscriptionEditionFormContext);
  const authContext = useContext(AuthenticationContext);
  const scenariosContext = useContext(ScenariosContext);

  const [isTypologiesToScenariosEnabled, setIsTypologiesToScenariosEnabled] = useState(
    !!(
      subscription.partnerSettings.prospects_typologies_to_scenarios &&
      Object.keys(subscription.partnerSettings.prospects_typologies_to_scenarios).length > 0
    )
  );
  const [isImportingCRMContacts, setIsImportingCRMContacts] = useState(false);
  const [isDryRunReport, setIsDryRunReport] = useState(false);
  const [snapshotStatus, setSnapshotStatus] = useState<GetCrmSnapshotStatusResponse['status']>('none');
  const [snapshotGeneratedAt, setSnapshotGeneratedAt] = useState<number | null>(null);
  const [isExplorerOpen, setIsExplorerOpen] = useState(false);
  const snapshotPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isBusy = useMemo(() => {
    return (
      formContext.updateStatus === ContextStatus.LOADING ||
      formContext.deleteStatus === ContextStatus.LOADING
    );
  }, [formContext.updateStatus, formContext.deleteStatus]);

  const isReadOnly =
    authContext.authentication == null ||
    ![UserRole.owner, UserRole.admin, UserRole.maintainer].includes(
      authContext.authentication?.role
    );

  const wipSubscription = formContext.wipSubscription;

  const subscriptionScenarios = useMemo(() => {
    return scenariosContext.scenarios.filter((scenario) =>
      (formContext.wipSubscription?.idScenarios || []).includes(scenario.id)
    );
  }, [formContext.wipSubscription, scenariosContext.scenarios]);

  const typologiesDataSource = useMemo(() => {
    const subscriptionTypes = Object.entries(
      ProspectType[wipSubscription?.partnerSystem as PartnersType] || {}
    ).map((entry) => {
      return {
        label: prospectTypeAsString(entry[1], wipSubscription?.partnerSystem as PartnersType),
        value: entry[1],
      };
    });

    const customProspectTypes = formContext.customProspectTypes.map((typology) => ({
      label: typology.name,
      value: typology.id,
    }));

    const res: { label: string; title: string; options: { label: string; value: string | number }[] }[] = [
      { label: 'Typologies par défaut', title: 'default', options: subscriptionTypes },
    ];

    if (customProspectTypes.length > 0) {
      res.push({ label: 'Typologies customs', title: 'custom', options: customProspectTypes });
    }
    return res;
  }, [formContext.customProspectTypes]);

  const typologiesToScenariosDataSource = useMemo(() => {
    return (
      wipSubscription?.partnerSettings?.prospects_typologies_to_scenarios &&
      wipSubscription?.partnerSettings?.prospects_typologies?.map((typology) => ({
        typology,
        scenario: wipSubscription.partnerSettings?.prospects_typologies_to_scenarios?.[typology],
      }))
    );
  }, [wipSubscription]);

  const handleImportCRMContacts = () => {
    if (authContext.authentication === null) {
      return;
    }

    setIsImportingCRMContacts(true);
    importCRMContacts(
      {
        apiKey: subscription.apiKey,
        emailCc: authContext.authentication.email.trim() ?? undefined,
      },
      authContext.authentication
    )
      .then((response) => {
        message.success(response.data?.message || 'Import démarré avec succès');
      })
      .catch((error) => {
        message.error(error.message || "Erreur lors de l'import");
      })
      .finally(() => {
        setIsImportingCRMContacts(false);
      });
  };

  const handleDryRunReport = () => {
    if (authContext.authentication === null || wipSubscription === null) {
      return;
    }

    setIsDryRunReport(true);
    getDryRunReport({
      apiKey: subscription.apiKey,
      emailCc: authContext.authentication.email.trim() ?? undefined,
      partner_settings: wipSubscription.partnerSettings
    }, authContext.authentication)
      .then((response) => {
        message.success(response.message || 'Rapport démarré avec succès');
      })
      .catch((error) => {
        message.error(error.message || "Erreur lors de l'obtention du rapport");
      })
      .finally(() => {
        setIsDryRunReport(false);
      });
  }

  const fetchSnapshotStatus = useCallback(() => {
    if (!authContext.authentication || !subscription.apiKey) return;
    getCrmSnapshotStatus({ apiKey: subscription.apiKey }, authContext.authentication)
      .then((response) => {
        const status = response?.status || 'none';
        setSnapshotStatus(status);
        if (response?.generatedAt) {
          setSnapshotGeneratedAt(response.generatedAt);
        }
        // Stop polling when no longer generating
        if (status !== 'generating' && snapshotPollRef.current) {
          clearInterval(snapshotPollRef.current);
          snapshotPollRef.current = null;
        }
      })
      .catch(() => {
        // Silently fail, snapshot feature is non-critical
      });
  }, [authContext.authentication, subscription.apiKey]);

  const handleGenerateSnapshot = () => {
    if (!authContext.authentication) return;
    setSnapshotStatus('generating');
    notification.info({
      message: 'Téléchargement lancé',
      description: 'Le téléchargement de la base de contacts CRM a été lancé, celui-ci prend habituellement entre quelques minutes et une demi-heure.',
      duration: 8,
      placement: 'bottom',
    });
    generateCrmSnapshot({ apiKey: subscription.apiKey }, authContext.authentication)
      .then(() => {
        // Start polling
        if (!snapshotPollRef.current) {
          snapshotPollRef.current = setInterval(fetchSnapshotStatus, 10000);
        }
      })
      .catch((error) => {
        message.error(error.message || 'Erreur lors du téléchargement des contacts');
        setSnapshotStatus('none');
      });
  };

  const handleTypologiesToScenariosPositionChange = (typology: string, movement: 'up' | 'down') => {
    const currentTypologies = wipSubscription?.partnerSettings?.prospects_typologies || [];

    const currentIndex = currentTypologies.findIndex((value) => value === typology);

    if (currentIndex === -1) return;

    // Calculate new index based on movement
    let newIndex: number;
    if (movement === 'up' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else if (movement === 'down' && currentIndex < currentTypologies.length - 1) {
      newIndex = currentIndex + 1;
    } else {
      return;
    }

    const newTypologies = [...currentTypologies];
    const [movedEntry] = newTypologies.splice(currentIndex, 1);
    newTypologies.splice(newIndex, 0, movedEntry);

    // Update the subscription with new order
    if (wipSubscription) {
      formContext.onPartnerSettingsProspectsTypologiesChange(newTypologies as string[]);
    }
  };

  useEffect(() => {
    if (formContext.wipSubscription === null) {
      formContext.initWithSubscription(subscription);
    }
  }, []);

  useEffect(() => {
    formContext.fetchPartnerUserUuids();
    formContext.fetchPartnerCustomProspectTypes();
  }, [formContext.wipSubscription]);

  useEffect(() => {
    switch (formContext.updateStatus) {
      case ContextStatus.SUCCESS:
        message.success('Mise à jour effectuée');
        break;
      case ContextStatus.ERROR:
        message.error('Erreur lors de la mise à jour');
        break;
      default:
        break;
    }
  }, [formContext.updateStatus]);

  // Fetch snapshot status on mount
  useEffect(() => {
    fetchSnapshotStatus();
    return () => {
      if (snapshotPollRef.current) {
        clearInterval(snapshotPollRef.current);
      }
    };
  }, [fetchSnapshotStatus]);

  // Start polling if already generating
  useEffect(() => {
    if (snapshotStatus === 'generating' && !snapshotPollRef.current) {
      snapshotPollRef.current = setInterval(fetchSnapshotStatus, 10000);
    }
  }, [snapshotStatus, fetchSnapshotStatus]);

  return wipSubscription === null ? (
    <Card loading />
  ) : (
    <FormCard
      title={wipSubscription.id}
      onUpdated={isReadOnly ? undefined : () => formContext.submitUpdate()}
      isUpdateLoading={isBusy}
    >
      <Form>
        {wipSubscription.partnerSystem !== '' && wipSubscription.partnerApiKey !== '' && (
          <div>
            <FormSectionTitle
              title={
                'Configuration de la passerelle ' +
                partnersTypeAsString(wipSubscription?.partnerSystem as PartnersType)
              }
            />
            <Form.Item>
              <h4><ScheduleOutlined style={{ marginRight: 6 }} /> Actions</h4>
              <Flex gap={10}>
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  onClick={() => handleImportCRMContacts()}
                  loading={isImportingCRMContacts}
                >
                  Importer les contacts
                </Button>
                <Button
                  type="default"
                  icon={<MailOutlined />}
                  onClick={() => handleDryRunReport()}
                  loading={isDryRunReport}
                >
                  Obtenir un rapport
                </Button>

                {snapshotStatus === 'ready' ? (
                  <Tooltip title={snapshotGeneratedAt ? `Dernière mise à jour : ${new Date(snapshotGeneratedAt).toLocaleDateString('fr-FR')}` : undefined}>
                    <Button
                      type="default"
                      icon={<SearchOutlined />}
                      onClick={() => setIsExplorerOpen(true)}
                    >
                      Explorer la base de contacts CRM
                    </Button>
                  </Tooltip>
                ) : snapshotStatus === 'generating' ? (
                  <Button
                    type="default"
                    icon={<LoadingOutlined />}
                    disabled
                  >
                    Téléchargement base contact CRM en cours...
                  </Button>
                ) : (
                  <Tooltip title="Dans un premier temps la base complète des contacts sera téléchargée, vous pourrez ensuite l'explorer via l'outil interactif">
                    <Button
                      type="default"
                      icon={<CloudDownloadOutlined />}
                      onClick={handleGenerateSnapshot}
                    >
                      Explorer la base de contacts CRM
                    </Button>
                  </Tooltip>
                )}

              </Flex>

              <Alert
                style={{ margin: '1rem 0' }}
                showIcon
                closable={false}
                message={
                  <Flex vertical gap={6}>
                    <span>
                      Cette action va importer les derniers contacts de la passerelle{' '}
                      {partnersTypeAsString(wipSubscription?.partnerSystem as PartnersType)}.
                    </span>
                    <span>
                      Cette action peut durer plusieurs minutes et l'utilisation est limitée à 1
                      import à la fois.
                    </span>
                  </Flex>
                }
              />
            </Form.Item>

            <Form.Item>
              <h4><RocketOutlined style={{ marginRight: 6 }} /> Mode d'import</h4>
              <Radio.Group
                size="middle"
                optionType="button"
                value={wipSubscription?.partnerSettings?.import_mode}
                options={[
                  {
                    label: <Flex align="center" gap={6}><RobotOutlined /> Mode Automatique</Flex>,
                    value: 'auto',
                  },
                  {
                    label: '⚙️ Mode custom',
                    value: 'custom',
                  },
                  {
                    label: '✍️ Mode manuel',
                    value: 'manual',
                  },
                ]}
                onChange={(e) => {
                  formContext.onPartnerSettingsImportModeChange(e.target.value);
                }}
              />
              <Alert
                style={{ margin: '1rem 0' }}
                showIcon
                closable={false}
                message={
                  wipSubscription?.partnerSettings?.import_mode === 'auto'
                    ? "Mode automatique : L'IA utilisera des paramètres optimisés basés sur les meilleures pratiques du secteur immobilier."
                    : wipSubscription?.partnerSettings?.import_mode === 'custom'
                      ? 'Mode custom : vous avez le contrôle total sur tous les paramètres de configuration.'
                      : "Mode manuel : L'IA n'importera pas vos contacts."
                }
              />
            </Form.Item>
            {wipSubscription?.partnerSettings?.import_mode === 'custom' && (
              <>
                <Form.Item>
                  <h4><ContactsOutlined style={{ marginRight: 6 }} />1. Typologies des prospects à relancer</h4>
                  <Alert
                    style={{ margin: '1rem 0' }}
                    showIcon
                    message="Lorsqu'un prospect est associé à plusieurs typologies, le scénario associé sera la premiere typologie correspondante."
                  />
                  <Flex vertical gap={10} style={{ width: '500px' }}>
                    <Select
                      mode="multiple"
                      placeholder="Tous les prospects"
                      maxTagCount={2}
                      value={wipSubscription?.partnerSettings?.prospects_typologies}
                      options={typologiesDataSource}
                      onChange={(value) => {
                        formContext.onPartnerSettingsProspectsTypologiesChange(value);
                      }}
                      optionFilterProp="label"
                    />
                    {!isTypologiesToScenariosEnabled ? (
                      <Button type="link" onClick={() => setIsTypologiesToScenariosEnabled(true)}>
                        Ajouter des scénarios customs
                      </Button>
                    ) : null}
                    {isTypologiesToScenariosEnabled &&
                      (wipSubscription?.partnerSettings?.prospects_typologies?.length || 0) > 0 ? (
                      <Flex
                        vertical
                        style={{
                          border: '1px solid var(--border-color)',
                          padding: '1rem',
                          borderRadius: '.625rem',
                          boxSizing: 'border-box',
                          width: '100%',
                        }}
                      >
                        <Table
                          showHeader={false}
                          pagination={false}
                          style={{
                            width: '100%',
                          }}
                          dataSource={typologiesToScenariosDataSource}
                          rowKey="typology"
                          columns={[
                            {
                              title: 'Typologie',
                              dataIndex: 'typology',
                              key: 'typology',
                              width: 130,
                              render: (typology) => {
                                return formContext.customProspectTypes.find((customProspectType) => customProspectType.id === typology)?.name ?? prospectTypeAsString(
                                  typology,
                                  wipSubscription?.partnerSystem as PartnersType
                                );
                              },
                            },
                            {
                              title: 'Scénario',
                              dataIndex: 'scenario',
                              key: 'scenario',
                              width: 270,
                              render: (scenario, record) => {
                                return (
                                  <Select // Select wich scenario use for this typology
                                    size="small"
                                    style={{
                                      width: '100%',
                                      maxWidth: '270px',
                                    }}
                                    options={subscriptionScenarios.map((scenario) => {
                                      return {
                                        label: scenario.name,
                                        value: scenario.id,
                                      };
                                    })}
                                    onChange={(scenarioId) => {
                                      formContext.onPartnerSettingsProspectsTypologiesToScenariosChange(
                                        record.typology,
                                        scenarioId
                                      );
                                    }}
                                    value={
                                      wipSubscription?.partnerSettings
                                        ?.prospects_typologies_to_scenarios?.[record.typology] ??
                                      'Automatique'
                                    }
                                    optionRender={(option) => {
                                      return (
                                        <Flex vertical gap={4}>
                                          <span>{option.label}</span>
                                          <span
                                            style={{
                                              fontStyle: 'italic',
                                              fontWeight: 'normal',
                                            }}
                                          >
                                            {option.value}
                                          </span>
                                        </Flex>
                                      );
                                    }}
                                    allowClear
                                  />
                                );
                              },
                            },
                            {
                              title: 'Actions',
                              dataIndex: 'actions',
                              key: 'actions',
                              width: 60,
                              align: 'center',
                              render: (_, record, index) => {
                                return (
                                  <Flex vertical gap={4} align="center" justify="center">
                                    {index > 0 ? (
                                      <Button
                                        size="small"
                                        shape="circle"
                                        icon={<ArrowUpOutlined />}
                                        onClick={() =>
                                          handleTypologiesToScenariosPositionChange(
                                            record.typology,
                                            'up'
                                          )
                                        }
                                      />
                                    ) : null}

                                    {index < (typologiesToScenariosDataSource?.length || 0) - 1 ? (
                                      <Button
                                        size="small"
                                        shape="circle"
                                        icon={<ArrowDownOutlined />}
                                        onClick={() =>
                                          handleTypologiesToScenariosPositionChange(
                                            record.typology,
                                            'down'
                                          )
                                        }
                                      />
                                    ) : null}
                                  </Flex>
                                );
                              },
                            },
                          ]}
                        />
                      </Flex>
                    ) : null}
                  </Flex>
                </Form.Item>
                <Form.Item>
                  <h4><UserOutlined style={{ marginRight: 6 }} />2. Utilisateurs bénéficiaires</h4>
                  <Select
                    mode="multiple"
                    placeholder="Tous les commerciaux"
                    style={{ width: 450 }}
                    value={(wipSubscription?.partnerSettings?.users_uuids ?? []).filter((uuid) =>
                      formContext.partnerSettingsUserUuids.some((user) => user.partner_uid === uuid)
                    )}
                    loading={formContext.partnerStatus === ContextStatus.LOADING}
                    maxTagCount={2}
                    options={[
                      {
                        label: 'Tous les commerciaux',
                        value: 'all',
                      },
                      ...formContext.partnerSettingsUserUuids
                        .sort((a, b) => {
                          return a.firstname.toLowerCase().localeCompare(b.firstname.toLowerCase());
                        })
                        .filter((oSale) => !!oSale.partner_uid)
                        .map((oSale) => {
                          return {
                            label: <Flex align="center" gap={6}><UserOutlined /> ${oSale.firstname} ${oSale.lastname}</Flex>,
                            value: oSale.partner_uid,
                          };
                        }),
                    ]}
                    onChange={(value) => {
                      if (value.includes('all')) {
                        formContext.onPartnerSettingsUserUuidsChange([]);
                      } else {
                        formContext.onPartnerSettingsUserUuidsChange(value);
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item>
                  <h4><ClockCircleOutlined style={{ marginRight: 6 }} />3. Ancienneté</h4>
                  <Flex gap={16} wrap="wrap" align="flex-end">
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                        Temps d'inactivité minimum
                      </label>
                      <Select
                        style={{ width: 250 }}
                        placeholder="Temps d'inactivité minimum..."
                        value={wipSubscription?.partnerSettings?.prospects_seniority}
                        options={Object.entries(DelayType).map((entry) => {
                          return {
                            label: delayTypeAsString(entry[1]),
                            value: entry[1],
                          };
                        })}
                        onChange={(value) => {
                          formContext.onPartnerSettingsProspectsSeniorityChange(value);
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                        Temps d'inactivité maximum
                      </label>
                      <Select
                        style={{ width: 250 }}
                        placeholder="Temps d'inactivité maximum..."
                        value={wipSubscription?.partnerSettings?.prospects_seniority_max ?? "1095_days"}
                        options={[{ label: "1 an", value: "365_days" }, { label: "2 ans", value: "730_days" }, { label: "3 ans", value: "1095_days" }, { label: "5 ans", value: "1825_days" }, { label: "7 ans", value: "2555_days" }, { label: "Sans limite", value: "unlimited" }]}
                        onChange={(value) => {
                          formContext.onPartnerSettingsProspectsSeniorityMaxChange(value);
                        }}
                      />
                    </div>
                  </Flex>
                </Form.Item>
                <Form.Item>
                  <h4><SettingOutlined style={{ marginRight: 6 }} />4. Options avancées</h4>

                  {wipSubscription?.datasource === "septeo" && (
                    <Checkbox
                      checked={wipSubscription?.partnerSettings?.include_archived ?? false}
                      onChange={(e) => {
                        const value = e.target.checked;
                        formContext.onPartnerSettingsIncludeArchivedChange(value);
                      }}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <div>
                        <div style={{ paddingTop: "3px" }}>Inclure les contacts archivés</div>
                      </div>
                    </Checkbox>
                  )}

                  {[SubscriptionDataSource.septeo, SubscriptionDataSource.ac3].includes(wipSubscription?.datasource as SubscriptionDataSource) && (
                    <Checkbox
                      checked={!(wipSubscription?.partnerSettings?.include_buyers_without_identified_search ?? true)}
                      onChange={(e) => {
                        const value = !e.target.checked; // UI displays "exclude" but stored value is "include"
                        formContext.onPartnerSettingsIncludeBuyersWithoutIdentifiedSearchChange(value);
                      }}
                      style={{ display: "flex", alignItems: "center", marginTop: "0.5rem" }}
                    >
                      <div>
                        <div style={{ paddingTop: "3px" }}>Exclure les acheteurs sans fiche acquéreur</div>
                      </div>
                    </Checkbox>
                  )}

                  {[SubscriptionDataSource.septeo, SubscriptionDataSource.ac3].includes(wipSubscription?.datasource as SubscriptionDataSource) && (
                    <Checkbox
                      checked={!(wipSubscription?.partnerSettings?.include_sellers_without_estimation ?? true)}
                      onChange={(e) => {
                        const value = !e.target.checked; // UI displays "exclude" but stored value is "include"
                        formContext.onPartnerSettingsIncludeSellersWithoutEstimationChange(value);
                      }}
                      style={{ display: "flex", alignItems: "center", marginTop: "0.5rem" }}
                    >
                      <div>
                        <div style={{ paddingTop: "3px" }}>Exclure les vendeurs sans estimation</div>
                      </div>
                    </Checkbox>
                  )}

                  {[SubscriptionDataSource.septeo, SubscriptionDataSource.ac3].includes(wipSubscription?.datasource as SubscriptionDataSource) && (
                    <Checkbox
                      checked={wipSubscription?.partnerSettings?.include_contacts_without_typology ?? false}
                      onChange={(e) => {
                        const value = e.target.checked;
                        formContext.onPartnerSettingsIncludeContactsWithoutTypologyChange(value);
                      }}
                      style={{ display: "flex", alignItems: "center", marginTop: "0.5rem" }}
                    >
                      <div>
                        <div style={{ paddingTop: "3px" }}>Inclure les contacts sans typologie</div>
                      </div>
                    </Checkbox>
                  )}

                  {[SubscriptionDataSource.septeo, SubscriptionDataSource.ac3].includes(wipSubscription?.datasource as SubscriptionDataSource) && (
                    <Checkbox
                      checked={wipSubscription?.partnerSettings?.archive_prospects_without_interest ?? false}
                      onChange={(e) => {
                        const value = e.target.checked;
                        formContext.onPartnerSettingsArchiveProspectsWithoutInterestChange(value);
                      }}
                      style={{ display: "flex", alignItems: "center", marginTop: "0.5rem" }}
                    >
                      <div>
                        <div style={{ paddingTop: "3px" }}>Archiver automatiquement dans mon CRM les prospects qui ne sont plus en recherche</div>
                      </div>
                    </Checkbox>
                  )}

                  <div style={{ marginTop: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                      Nombre max de contacts à importer par mois
                    </label>
                    <Select
                      style={{ width: 250 }}
                      value={wipSubscription?.partnerSettings?.max_credit_consumption_per_month ?? 100}
                      options={[
                        { label: '50', value: 50 },
                        { label: '100', value: 100 },
                        { label: '150', value: 150 },
                        { label: '200', value: 200 },
                        { label: '300', value: 300 },
                        { label: '400', value: 400 },
                        { label: '500', value: 500 },
                        { label: '1000', value: 1000 },
                      ]}
                      onChange={(value) => {
                        formContext.onPartnerSettingsMaxCreditConsumptionPerMonthChange(value);
                      }}
                    />
                  </div>
                </Form.Item>
              </>
            )}
          </div>
        )}
      </Form>

      <CrmContactExplorerModal
        open={isExplorerOpen}
        onCancel={() => setIsExplorerOpen(false)}
        subscription={subscription}
        partnerSystem={wipSubscription?.partnerSystem || ''}
        partnerSettings={wipSubscription?.partnerSettings || null}
        snapshotGeneratedAt={snapshotGeneratedAt}
        onRefreshSnapshot={handleGenerateSnapshot}
      />
    </FormCard>
  );
};

export default CRMDetails;
